/**
 * Live Kitchen Queue
 *
 * Toast has no dedicated KDS or kitchen-stream API. We poll the Orders
 * bulk endpoint scoped to today's business date, flatten each
 * order/check's selections, and surface the ones still in the kitchen
 * workflow (NEW / SENT / HOLD). READY items are dropped because they've
 * left the line. Grouped by check so a multi-item ticket renders as one
 * card, sorted oldest first by the earliest selection's createdDate.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useToastAPI from '../hooks/useToastAPI';
import './Kitchen.css';

const REFRESH_INTERVAL_MS = 60 * 1000;
const ACTIVE_STATUSES = new Set(['NEW', 'SENT', 'HOLD']);

function todayBusinessDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function fmtElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${String(s).padStart(2, '0')}s`;
}

function fmtClock(ms) {
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function fmtDayHeader(ms) {
  const d = ms ? new Date(ms) : new Date();
  return d.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function ageTone(ms) {
  if (!Number.isFinite(ms)) return '';
  const min = ms / 60000;
  if (min >= 10) return 'is-bad';
  if (min >= 5) return 'is-warn';
  return 'is-ok';
}

function statusLabel(status) {
  switch (status) {
    case 'NEW': return 'New';
    case 'SENT': return 'Firing';
    case 'HOLD': return 'Hold';
    default: return status;
  }
}

function statusClass(status) {
  switch (status) {
    case 'NEW': return 'kitchen-status kitchen-status--new';
    case 'SENT': return 'kitchen-status kitchen-status--sent';
    case 'HOLD': return 'kitchen-status kitchen-status--hold';
    default: return 'kitchen-status';
  }
}

function diningOptionName(order) {
  const opt = order?.diningOption;
  if (!opt) return 'Dine-in';
  if (typeof opt === 'string') return opt;
  return opt.name || 'Dine-in';
}

function tableLabel(check, order) {
  if (check?.tabName) return check.tabName;
  if (order?.table?.name) return order.table.name;
  if (check?.customer?.firstName || check?.customer?.lastName) {
    return [check.customer.firstName, check.customer.lastName].filter(Boolean).join(' ');
  }
  return null;
}

function buildTickets(orders) {
  const tickets = [];
  for (const order of orders || []) {
    if (order?.voided || order?.deleted) continue;
    for (const check of order.checks || []) {
      if (check.voided || check.deleted) continue;
      const activeSelections = (check.selections || []).filter(sel => {
        if (sel.voided || sel.deleted) return false;
        return ACTIVE_STATUSES.has(sel.fulfillmentStatus);
      });
      if (activeSelections.length === 0) continue;

      const timestamps = activeSelections
        .map(sel => Date.parse(sel.createdDate || sel.modifiedDate || order.createdDate))
        .filter(Number.isFinite);
      const oldestMs = timestamps.length > 0 ? Math.min(...timestamps) : null;

      tickets.push({
        ticketKey: check.guid || `${order.guid}-${check.displayNumber || ''}`,
        ticketNumber: check.displayNumber || order.displayNumber || null,
        tabName: tableLabel(check, order),
        diningOption: diningOptionName(order),
        oldestMs,
        selections: activeSelections.map(sel => ({
          guid: sel.guid,
          name: sel.displayName || 'Unknown item',
          quantity: sel.quantity || 1,
          status: sel.fulfillmentStatus,
          modifiers: (sel.modifiers || [])
            .filter(m => !m.voided && !m.deleted)
            .map(m => m.displayName)
            .filter(Boolean),
          createdMs: Date.parse(sel.createdDate || sel.modifiedDate || order.createdDate),
        })),
      });
    }
  }
  tickets.sort((a, b) => (a.oldestMs ?? Infinity) - (b.oldestMs ?? Infinity));
  return tickets;
}

const Kitchen = () => {
  const toast = useToastAPI();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [now, setNow] = useState(Date.now());
  const inFlight = useRef(false);

  const fetchOrders = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const result = await toast.getOrdersBulk(todayBusinessDate());
      const list = Array.isArray(result) ? result : (result?.orders || result?.data || []);
      setOrders(list);
      setError(null);
      setLastFetchedAt(Date.now());
    } catch (err) {
      setError(err?.message || 'Failed to load orders');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchOrders();
    const id = setInterval(fetchOrders, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fetchOrders]);

  // Tick a wall clock once a second so elapsed times re-render without
  // refetching the orders payload.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const tickets = useMemo(() => buildTickets(orders), [orders]);

  const totals = useMemo(() => {
    let items = 0;
    let holds = 0;
    let sent = 0;
    for (const t of tickets) {
      for (const sel of t.selections) {
        items += sel.quantity || 1;
        if (sel.status === 'HOLD') holds += sel.quantity || 1;
        if (sel.status === 'SENT') sent += sel.quantity || 1;
      }
    }
    return { items, holds, sent };
  }, [tickets]);

  const lastSyncLabel = lastFetchedAt
    ? `${Math.max(0, Math.round((now - lastFetchedAt) / 1000))}s ago`
    : '—';

  return (
    <div className="kitchen-page">
      <header className="kitchen-header">
        <div>
          <h1 className="kitchen-title">Live Kitchen Queue</h1>
          <p className="kitchen-subtitle">
            Polled from Toast Orders every {REFRESH_INTERVAL_MS / 1000}s · last sync {lastSyncLabel}
          </p>
        </div>
        <div className="kitchen-stats">
          <div className="kitchen-stat">
            <span className="kitchen-stat-label">Active tickets</span>
            <span className="kitchen-stat-value">{tickets.length}</span>
          </div>
          <div className="kitchen-stat">
            <span className="kitchen-stat-label">Items in flight</span>
            <span className="kitchen-stat-value">{totals.items}</span>
          </div>
          <div className="kitchen-stat">
            <span className="kitchen-stat-label">Firing</span>
            <span className="kitchen-stat-value">{totals.sent}</span>
          </div>
          <div className={`kitchen-stat ${totals.holds > 0 ? 'is-warn' : ''}`}>
            <span className="kitchen-stat-label">On hold</span>
            <span className="kitchen-stat-value">{totals.holds}</span>
          </div>
          <button
            type="button"
            className="kitchen-refresh"
            onClick={fetchOrders}
            disabled={inFlight.current}
            title="Refresh now"
          >
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="kitchen-error">
          Couldn’t reach Toast: {error}
        </div>
      )}

      {loading && tickets.length === 0 ? (
        <div className="kitchen-empty">
          <div className="kitchen-empty-icon">🍳</div>
          <div className="kitchen-empty-text">Loading today’s orders…</div>
        </div>
      ) : tickets.length === 0 ? (
        <div className="kitchen-empty">
          <div className="kitchen-empty-icon">✨</div>
          <div className="kitchen-empty-text">All clear</div>
          <div className="kitchen-empty-subtext">
            No active selections (NEW / SENT / HOLD) on today’s tickets.
            If your venue doesn’t use Toast KDS, selections may never report status, so this page will look empty.
          </div>
        </div>
      ) : (
        <section className="kitchen-day">
          <h2 className="kitchen-day-header">{fmtDayHeader(tickets[0]?.oldestMs)}</h2>
          <ol className="kitchen-list">
            {tickets.map(ticket => {
              const elapsed = ticket.oldestMs ? now - ticket.oldestMs : null;
              const tone = ageTone(elapsed);
              return (
                <li key={ticket.ticketKey} className={`kitchen-row ${tone}`}>
                  <div className="kitchen-row-time">
                    <span className="kitchen-row-clock">{fmtClock(ticket.oldestMs)}</span>
                    <span className={`kitchen-row-age ${tone}`}>{fmtElapsed(elapsed)}</span>
                  </div>
                  <div className="kitchen-row-id">
                    <span className="kitchen-row-ticket">
                      {ticket.ticketNumber ? `#${ticket.ticketNumber}` : 'Ticket'}
                    </span>
                    {ticket.tabName && (
                      <span className="kitchen-row-tab">{ticket.tabName}</span>
                    )}
                    <span className="kitchen-row-dining">{ticket.diningOption}</span>
                  </div>
                  <ul className="kitchen-row-items">
                    {ticket.selections.map(sel => (
                      <li key={sel.guid} className="kitchen-row-item">
                        <span className="kitchen-selection-qty">{sel.quantity}×</span>
                        <span className="kitchen-selection-name">{sel.name}</span>
                        {sel.modifiers.length > 0 && (
                          <span className="kitchen-row-modifiers"> — {sel.modifiers.join(', ')}</span>
                        )}
                        <span className={statusClass(sel.status)}>{statusLabel(sel.status)}</span>
                      </li>
                    ))}
                  </ul>
                </li>
              );
            })}
          </ol>
        </section>
      )}
    </div>
  );
};

export default Kitchen;
